import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {Filter, FilterKey, keyToEmptyFilter} from 'search/SearchConstants';
import {
  getMethodSortKeyForProcedureType,
  getRunTaskFilterKeyForProcedureType,
  getUnitOperationModelTypeConstantsForProcedureType,
} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import CollapsibleCard from 'ui-library/CollapsibleCard';
import {WHITE} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {assertNever} from 'util/Types';

import StudyItemsTabButton from '../../../utils/ui/StudyItemsTabButton';
import {StudyInaccessibleContentBanner} from '../../../utils/ui/StudyQueryBanners';
import {
  ProcedureRunOrPlanStateSummary as ProcedureRunStateSummary,
  ProcedureRunTab,
} from '../overview/MultiProcedureRunAndPlanOverviewTabContents';
import MultiProcedureRunStudyExecutionProgress from '../overview/MultiProcedureRunStudyExecutionProgress';
import {
  buildMultiProcedureRunStudyExecutionProgress,
  doesProcedureRunHaveAccessibleData,
} from '../overview/procedureRunOverviewUtils';
import {
  ProcedureRunDetailsViewQuery,
  ProcedureRunDetailsViewQuery_procedureRun_ProcedureRunNode as ProcedureRunNode,
  ProcedureRunDetailsViewQueryVariables,
} from './__generated/ProcedureRunDetailsViewQuery';
import ProcedureRunDesignTabContents from './ProcedureRunDesignTabContents';

export const PROCEDURE_RUN_DETAILS_VIEW_QUERY: DocumentNode = gql`
  query ProcedureRunDetailsViewQuery($procedureRunId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $procedureRunId) {
      ... on ProcedureRunNode {
        id
        name
        description
        displayId
        createdAt
        procedureType
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            nodes {
              id
              name
            }
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  id
                }
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ...ProcedureFlowchartConfigVersionProxyGraphQL
          ... on InaccessibleInfoNode {
            ...InaccessibleInfoGraphQL
          }
        }
        conditions {
          id
          name
          description
          templateMethodVersion {
            ... on ProcedureMethodDefinitionVersionNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
          procedureRunConditionReplicates {
            id
            replicateNumber
            processRootTask {
              ... on ProcessTaskNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
        methodExecutionInstances {
          id
          archiveData {
            archived
          }
          templateMethodVersion {
            ... on ProcedureMethodDefinitionVersionNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
          sourceProcedureMethodExecutionInstanceAssociations {
            ... on ProcedureMethodExecutionInstanceAssociationNode {
              id
              sourceProcedureMethodExecutionInstance {
                ... on ProcedureMethodExecutionInstanceNode {
                  id
                }
              }
            }
            ...InaccessibleInfoGraphQL
          }
          procedureRunConditionReplicate {
            id
            replicateNumber
            procedureRunCondition {
              id
            }
          }
          worksheet {
            ... on WorksheetNode {
              id
              name
              createdAt
              editUrl
            }
            ...InaccessibleInfoGraphQL
          }
          methodTask {
            ... on ProcessTaskNode {
              id
              status {
                ... on ProcessTaskStatusNode {
                  id
                  statusType
                }
              }
            }
            ...InaccessibleInfoGraphQL
          }
          previousProcedureMethodExecutionInstances {
            ... on ProcedureMethodExecutionInstanceNode {
              id
              methodTask {
                ... on ProcessTaskNode {
                  id
                  status {
                    ... on ProcessTaskStatusNode {
                      id
                      statusType
                    }
                  }
                }
                ...InaccessibleInfoGraphQL
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ProcedureFlowchartConfigVersionProxy').fragment}
`;

interface ProcedureRunDetailsViewProps {
  studyType: StudyType;
  studyId: string;
  selectedProcedureRun: ProcedureRunStateSummary;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}

export default function ProcedureRunDetailsView({
  studyType,
  studyId,
  selectedProcedureRun,
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
}: ProcedureRunDetailsViewProps): JSX.Element {
  const result = useSkippableBenchlingQuery<
    ProcedureRunDetailsViewQuery,
    ProcedureRunDetailsViewQueryVariables
  >(PROCEDURE_RUN_DETAILS_VIEW_QUERY, {
    skip: selectedProcedureRun.id == null,
    variables: {procedureRunId: selectedProcedureRun.id || ''},
  });

  if (result.type === QueryResultType.PENDING) {
    return (
      <>
        <div className={css(styles.loadingDiv)} />
        <CenteredHorizontalThrobber />
      </>
    );
  } else if (result.type === QueryResultType.ERROR || result.data == null) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="An error occurred while loading the procedure run data. Refresh the page and try again."
      />
    );
  } else {
    const {data} = result;
    if (isInaccessible(data.procedureRun)) {
      return (
        <StudyInaccessibleContentBanner
          inaccessibleInfo={data.procedureRun}
          modelType={ModelTypeConstants.PROCEDURE_RUN}
          displayErrorText="This procedure run is not accessible. Try refreshing the page if this is unexpected."
        />
      );
    }

    if (!doesProcedureRunHaveAccessibleData(data.procedureRun)) {
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Unable to display procedure run details. The procedure template or flowchart version is not accessible."
        />
      );
    }

    return (
      <RenderProcedureRunDetailsView
        studyType={studyType}
        studyId={studyId}
        procedureRun={data.procedureRun}
        currentProcedureRunTab={currentProcedureRunTab}
        setCurrentProcedureRunTab={setCurrentProcedureRunTab}
      />
    );
  }
}

interface RenderProcedureRunDetailsViewProps {
  studyType: StudyType;
  studyId: string;
  procedureRun: ProcedureRunNode;
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (tab: ProcedureRunTab) => void;
}

function getAdditionalFilters(procedureRun: ProcedureRunNode): Array<Filter> {
  const additionalFilters: Array<Filter> = [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [getUnitOperationModelTypeConstantsForProcedureType(procedureRun.procedureType)],
    },
  ];
  const rootTaskIds = new Set(
    procedureRun.conditions.flatMap((condition) =>
      condition.procedureRunConditionReplicates
        .map((replicate) =>
          replicate.processRootTask && !isInaccessible(replicate.processRootTask)
            ? replicate.processRootTask?.id
            : null
        )
        .filter((taskId): taskId is string => taskId != null)
    )
  );
  if (rootTaskIds.size > 0) {
    additionalFilters.push({
      ...keyToEmptyFilter(getRunTaskFilterKeyForProcedureType(procedureRun.procedureType)),
      value: [...rootTaskIds],
    } as Filter);
  }
  return additionalFilters;
}

function RenderProcedureRunDetailsView({
  studyType,
  studyId,
  procedureRun,
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
}: RenderProcedureRunDetailsViewProps): JSX.Element {
  switch (currentProcedureRunTab) {
    case ProcedureRunTab.OVERVIEW:
      return (
        <div className={css(styles.procedureRunDetails)}>
          <CollapsibleCard
            initiallyExpanded={true}
            isCollapsible={false}
            headerColor={WHITE}
            leftHeader={
              <div
                className={css(styles.unitOperationsTitle)}
                {...getTestProps({component: 'ProcedureRunDetailsView', element: 'unitOperationsTitle'})}
              >
                Unit operation instances
              </div>
            }
            rightHeader={
              <StudyItemsTabButton
                studyId={studyId}
                style={ButtonStyle.TEXT}
                additionalFilters={getAdditionalFilters(procedureRun)}
                sorts={[{key: getMethodSortKeyForProcedureType(procedureRun.procedureType), reverse: false}]}
                text="See detailed view"
              />
            }
          >
            <MultiProcedureRunStudyExecutionProgress
              overview={buildMultiProcedureRunStudyExecutionProgress(procedureRun)}
              studyType={studyType}
            />
          </CollapsibleCard>
        </div>
      );
    case ProcedureRunTab.DESIGN:
      return (
        <ProcedureRunDesignTabContents
          procedureRunId={procedureRun.id}
          studyType={studyType}
          studyId={studyId}
          setCurrentProcedureRunTab={setCurrentProcedureRunTab}
        />
      );
    default:
      assertAndContinue(false, `Unexpected tab state ${assertNever(currentProcedureRunTab)}`);
      return (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Unexpected tab state. Refresh the page and try again."
        />
      );
  }
}

const styles = StyleSheet.create({
  unitOperationsTitle: {fontSize: 16},
  loadingDiv: {paddingBottom: 5},
  procedureRunDetails: {margin: 12},
});
